import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputBase,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { apiAxios } from "../../../api/axios";
import { useSnackbar } from "../../../common/context/SnackbarProvider";
import { smallGap } from "../../../styles/consts";
import { ChangeType, useEmailFeedback } from "../../context/EmailFeedbackContext";

const CHANGE_TYPE_OPTIONS: { value: ChangeType; label: string }[] = [
  { value: "minor_wording", label: "Minor wording/grammar adjustments" },
  { value: "factual_error", label: "Factual corrections (hallucinations)" },
  { value: "tone_style", label: "Tone/style changes" },
  { value: "added_info", label: "Added missing information" },
  { value: "removed_info", label: "Removed incorrect information" },
  { value: "structural", label: "Structural reorganization" },
];

interface EmailFeedbackModalProps {
  matchId: string;
  onClose: () => void;
}

export default function EmailFeedbackModal({
  matchId,
  onClose,
}: EmailFeedbackModalProps) {
  const {
    showFeedbackPopup,
    setShowFeedbackPopup,
    pendingNavigation,
    setPendingNavigation,
    getQualityScore,
    originalEmail,
    currentEmail,
    resetFeedbackState,
  } = useEmailFeedback();

  const { showSnackbar } = useSnackbar();

  const [selectedChangeTypes, setSelectedChangeTypes] = useState<ChangeType[]>([]);
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Editable quality score
  const autoCalculatedScore = getQualityScore();
  const [isEditingScore, setIsEditingScore] = useState(false);
  const [manualScore, setManualScore] = useState<number | null>(null);
  const scoreInputRef = useRef<HTMLInputElement>(null);

  // Use manual score if set, otherwise auto-calculated
  const qualityScore = manualScore !== null ? manualScore : autoCalculatedScore;

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingScore && scoreInputRef.current) {
      scoreInputRef.current.focus();
      scoreInputRef.current.select();
    }
  }, [isEditingScore]);

  const handleScoreClick = () => {
    setIsEditingScore(true);
  };

  const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string for editing
    if (value === "") {
      setManualScore(0);
      return;
    }
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      // Clamp to 0-100
      setManualScore(Math.max(0, Math.min(100, numValue)));
    }
  };

  const handleScoreBlur = () => {
    setIsEditingScore(false);
  };

  const handleScoreKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Escape") {
      setIsEditingScore(false);
    }
  };

  const handleChangeTypeToggle = (changeType: ChangeType) => {
    setSelectedChangeTypes((prev) =>
      prev.includes(changeType)
        ? prev.filter((ct) => ct !== changeType)
        : [...prev, changeType]
    );
  };

  const handleSkip = () => {
    setShowFeedbackPopup(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
    resetFeedbackState();
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const feedbackData = {
        matchId,
        qualityScore,
        changeTypes: selectedChangeTypes,
        comments,
        originalEmail,
        finalEmail: currentEmail,
        submittedAt: new Date().toISOString(),
      };

      // Send to backend
      const response = await apiAxios.post("/email-metrics", feedbackData);

      if (response.data.success) {
        showSnackbar("Feedback submitted successfully");
      } else {
        console.warn("Email metrics submission returned:", response.data);
        // Still proceed even if backend fails - don't block user
        showSnackbar("Feedback noted");
      }

      setShowFeedbackPopup(false);
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
      resetFeedbackState();
      onClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      // Don't block user navigation even if submission fails
      showSnackbar("Feedback noted (sync pending)");

      setShowFeedbackPopup(false);
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
      resetFeedbackState();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Treat closing the dialog as skipping
    handleSkip();
  };

  const getScoreColor = (score: number): string => {
    if (score > 80) return "#4caf50"; // Green (81-100%)
    if (score > 50) return "#ff9800"; // Orange/Yellow (51-80%)
    return "#f44336"; // Red (0-50%)
  };

  return (
    <Dialog
      open={showFeedbackPopup}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 0,
          backgroundColor: "#ffffff",
          boxShadow: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.25rem",
          fontWeight: 500,
          paddingBottom: "1rem",
        }}
      >
        Email Quality Feedback
      </DialogTitle>
      <DialogContent
        sx={{
          padding: "1rem",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <Stack sx={{ gap: smallGap }}>
          <Typography variant="body2" color="text.secondary">
            We detected you made changes to the email. Help us improve by
            providing feedback.
          </Typography>

          {/* Quality Score Display */}
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              padding: "1rem",
              borderRadius: "4px",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" fontWeight={500}>
                Quality Score
              </Typography>
              {isEditingScore ? (
                <ClickAwayListener onClickAway={handleScoreBlur}>
                  <Box sx={{ display: "flex", alignItems: "baseline" }}>
                    <InputBase
                      inputRef={scoreInputRef}
                      value={qualityScore}
                      onChange={handleScoreChange}
                      onKeyDown={handleScoreKeyDown}
                      onBlur={handleScoreBlur}
                      type="number"
                      inputProps={{
                        min: 0,
                        max: 100,
                        style: {
                          textAlign: "right",
                          width: "28px",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: getScoreColor(qualityScore),
                          padding: 0,
                          MozAppearance: "textfield",
                        },
                      }}
                      sx={{
                        borderBottom: `1px solid ${getScoreColor(qualityScore)}`,
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                          display: "none",
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: getScoreColor(qualityScore), fontWeight: 600 }}
                    >
                      %
                    </Typography>
                  </Box>
                </ClickAwayListener>
              ) : (
                <Tooltip title="Click to edit" arrow placement="left">
                  <Typography
                    variant="h6"
                    onClick={handleScoreClick}
                    sx={{
                      color: getScoreColor(qualityScore),
                      fontWeight: 600,
                      cursor: "pointer",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {qualityScore}%
                  </Typography>
                </Tooltip>
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={qualityScore}
              sx={{
                mt: 1,
                height: 8,
                borderRadius: 4,
                backgroundColor: "#e0e0e0",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: getScoreColor(qualityScore),
                  borderRadius: 4,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {manualScore !== null
                ? `Manually set (auto-calculated: ${autoCalculatedScore}%)`
                : "Auto-calculated based on your edits (click to edit)"}
            </Typography>
          </Box>

          {/* Change Types */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              What type of changes did you make? (select all that apply)
            </Typography>
            <Stack>
              {CHANGE_TYPE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={selectedChangeTypes.includes(option.value)}
                      onChange={() => handleChangeTypeToggle(option.value)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">{option.label}</Typography>
                  }
                  sx={{ marginY: -0.5 }}
                />
              ))}
            </Stack>
          </Box>

          {/* Additional Comments */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Additional comments (optional)
            </Typography>
            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder="Any specific details about what needed to be changed..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={isSubmitting}
              size="small"
            />
          </Box>

          {/* Actions */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            gap={1}
            sx={{ mt: 1 }}
          >
            <Button
              variant="outlined"
              onClick={handleSkip}
              disabled={isSubmitting}
            >
              Skip
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit & Continue"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
